/**
 * Per-guard awareness update — vision rise, sound bump, decay, state ladder,
 * investigation targets, ghost marker maintenance. See CLAUDE.md > Awareness.
 *
 * Consumes precomputed sound floods so we flood once per sound event and
 * check each guard against the result (O(events + guards*|events|) rather
 * than O(events × guards)).
 *
 * Headless — no PixiJS. See CLAUDE.md architecture rule 4.
 */

import { CONFIG } from '../config';
import { exposure } from './disguise';
import { isBlinded } from './blind';
import { coverShadowMargin, hasCoverBetween } from './cover';
import { computeVisionCone } from './fov';
import type { Guard, GuardState } from './guard';
import { inBounds, tileAt, type MapData } from './map';
import type { Player } from './player';
import { anyLightsOff, isUnlit, lightLevelAt, perceivedLight, visibilityFactor } from './light';
import { floodSound } from './sound';
import type { SoundEvent } from './soundQueue';
import { blocksVision, blocksVisionCrouched, TileType } from './tiles';

export interface ProcessedSound {
  event: SoundEvent;
  residual: Map<number, number>;
}

/** Preflood each event once. Callers pass the result to `updateGuardAwareness`. */
export function preprocessSounds(
  events: Iterable<SoundEvent>,
  map: MapData,
): ProcessedSound[] {
  const out: ProcessedSound[] = [];
  for (const e of events) {
    out.push({ event: e, residual: floodSound(e.x, e.y, e.loudness, map) });
  }
  return out;
}

/**
 * Update one guard's awareness meter, state, facing, investigation target,
 * and cached FOV. Runs AFTER the player has moved and AFTER sounds for this
 * tick have been preprocessed; runs BEFORE guard movement / wander.
 */
export function updateGuardAwareness(
  guard: Guard,
  player: Player,
  sounds: ProcessedSound[],
  map: MapData,
  tickDt: number,
  /** Extra sight-blockers that aren't tiles — steam from a burst boiler. */
  fog?: (x: number, y: number) => boolean,
): void {
  // 1. Recompute the guard's vision cone. A CROUCHED guard (ducking behind
  //    cover) is at waist height: cover is fully opaque to it (blocksVisionCrouched,
  //    no foot-shadow), and its range/angle shrink — so it can't see the player
  //    over cover and won't fire until it stands/peeks. A standing guard sees
  //    over cover with the one-tile foot shadow.
  // Distraction: on routine patrol a guard occasionally "zones out" — its sight
  // range collapses for a couple of seconds, then snaps back. Advanced here so
  // the shortened range takes effect the same tick it starts.
  updateDistraction(guard);
  const distractMult = guard.distractedTicks > 0 ? CONFIG.guard.distract.rangeMult : 1;
  const crouched = guard.stance === 'crouch';
  const range =
    CONFIG.vision.guardRange * (crouched ? CONFIG.vision.crouchRangeMult : 1) * distractMult;
  const halfAngle = CONFIG.vision.halfAngleRad * (crouched ? CONFIG.vision.crouchAngleMult : 1);
  const fov = new Set<number>();
  computeVisionCone(
    guard.x,
    guard.y,
    guard.facingDx,
    guard.facingDy,
    range,
    halfAngle,
    (x, y) => fog?.(x, y) === true
      || (crouched ? blocksVisionCrouched(tileAt(map, x, y)) : blocksVision(tileAt(map, x, y))),
    (x, y) => fov.add(y * map.width + x),
  );
  if (!crouched) coverShadowMargin(map, guard.x, guard.y, fov);
  guard.fovTiles = fov;

  // 2. Player visibility. Standing/running/walking targets are visible if in FOV;
  //    a crouched target additionally requires no partial cover (cubicle
  //    partition OR half wall) on the sightline. A crouched body is waist-high,
  //    so a half wall the guard would otherwise see over instead breaks the
  //    line of sight — cover behind a half wall actually conceals a croucher.
  const playerIdx = player.y * map.width + player.x;
  const dx = player.x - guard.x;
  const dy = player.y - guard.y;
  const cheb = Math.max(Math.abs(dx), Math.abs(dy));
  const crouchHidden =
    player.stance === 'crouch' && hasCoverBetween(map, guard.x, guard.y, player.x, player.y);
  // Lighting: a player in the dark is hard/impossible to spot. The light on the
  // player's tile gives a 0..1 visibility factor — 0 in shadow up to 1 in a lit
  // room. It gates cone-seeing AND scales how fast awareness rises. A guard with
  // NIGHT VISION perceives the tile `nvBoost` brighter, so it spots the player
  // even in the dark.
  const lightVis = visibilityFactor(perceivedLight(map, player.x, player.y, guard.nightVision));
  // Close-proximity "6th sense": a target right beside the guard is sensed
  // regardless of light or facing (but crouching behind cover still hides it).
  const nearSense = !crouchHidden && cheb <= CONFIG.vision.proximityTiles;
  // Overall detection multiplier (0 = unseen, 1 = full). A flash-blinded guard
  // sees nothing — its cone is whited out — so it can't spot the player at all
  // (sound still reaches it; being blind doesn't deafen).
  let visMul = 0;
  if (player.invisible) {
    // Debug ghost: nobody can see you. Sound still works — you are invisible,
    // not silent.
  } else if (player.concealedAt !== null) {
    // Shut inside a cabinet (prop 'hide'): no cone, no light, no 6th sense finds
    // you — but a guard already ALERT and standing on the prop hauls the door
    // open. Hiding beats a search, not someone who watched you climb in.
    if (guard.state === 'alert' && cheb <= 1) visMul = 1;
  } else if (!crouchHidden && !isBlinded(guard)) {
    if (nearSense) visMul = 1;
    else if (fov.has(playerIdx) && lightVis > 0) visMul = lightVis;
    // A UNIFORM doesn't hide you — it changes the question from "can I see
    // someone" to "do they belong here". `exposure` scores that (area, behaviour,
    // proximity) and scales the meter, so a convincing disguise buys the seconds
    // you need to walk out of the room rather than making you invisible.
    visMul *= exposure(player, map, guard.x, guard.y);
  }
  // A corpse raises no alarm — a dead player never counts as a sighting, so
  // awareness decays and guards return to patrol (see updateEliminationStandDown).
  const sees = visMul > 0 && !player.body.dead;
  guard.seesPlayerNow = sees;

  let hadStimulus = false;

  // 3. Vision-based awareness rise + facing update + Sniper-Elite "seen" ghost.
  if (sees) {
    hadStimulus = true;
    const dist = Math.hypot(dx, dy) || 1;
    // Proximity: 1.0 at close range, 0.3 at max range.
    const proximity = Math.max(0.3, 1 - (dist / CONFIG.vision.guardRange) * 0.7);
    const stanceMult = CONFIG.awareness.stanceMult[player.stance];
    guard.awareness = Math.min(
      CONFIG.awareness.max,
      guard.awareness + CONFIG.awareness.baseRisePerSec * proximity * stanceMult * visMul * tickDt,
    );
    guard.lastKnownPlayerX = player.x;
    guard.lastKnownPlayerY = player.y;
    guard.investigationX = player.x;
    guard.investigationY = player.y;
    // Drop / refresh the ghost at the seen tile; it commits to revisiting here.
    guard.sightingGhostTicks = Math.round(CONFIG.awareness.sightingGhostSec / tickDt);
    faceToward(guard, dx, dy);
  } else if (guard.sightingGhostTicks > 0) {
    // Contact broken: the ghost freezes at last-known and fades over ~5s.
    guard.sightingGhostTicks -= 1;
  }

  // 4. Sound events. Received loudness = residual at the guard's tile.
  //    Skip events this guard emitted — self-hearing would spike own meter.
  const idx = guard.y * map.width + guard.x;
  for (const s of sounds) {
    if (s.event.emitterId === guard.id) continue;
    const rec = s.residual.get(idx);
    if (rec === undefined || rec <= 0) continue;
    hadStimulus = true;
    guard.awareness = Math.min(
      CONFIG.awareness.max,
      guard.awareness + rec * CONFIG.sound.awarenessBumpPerLoudness,
    );
    guard.investigationX = s.event.x;
    guard.investigationY = s.event.y;
    if (guard.moveProgress === 0) faceToward(guard, s.event.x - guard.x, s.event.y - guard.y);
  }

  // 5. Decay when no stimulus this tick.
  if (!hadStimulus) {
    guard.awareness = Math.max(0, guard.awareness - CONFIG.awareness.decayPerSec * tickDt);
  }

  // 6. State ladder.
  const thr = CONFIG.awareness.thresholds;
  const prevState = guard.state;
  if (guard.awareness >= thr.alert) guard.state = 'alert';
  else if (guard.awareness >= thr.cautious) guard.state = 'cautious';
  else if (guard.awareness >= thr.suspicious) guard.state = 'suspicious';
  else guard.state = 'unaware';

  // 7. When fully calmed, clear ghost + investigation.
  if (guard.state === 'unaware' && prevState !== 'unaware') {
    guard.lastKnownPlayerX = null;
    guard.lastKnownPlayerY = null;
    guard.investigationX = null;
    guard.investigationY = null;
    guard.sightingGhostTicks = 0;
  }
}

/**
 * Tick the guard's distraction timer. While ON routine patrol (unaware and fully
 * calm, not mid-scan, not the captain, not on any special duty) a guard now and
 * then zones out: its sight range collapses for a couple of seconds (see the
 * range multiplier in `updateGuardAwareness`), then it returns to normal and the
 * next zone-out is spaced out by a randomised interval. Any stimulus (awareness
 * rising above 0 → not "calm") snaps it straight back to full vision.
 */
function updateDistraction(guard: Guard): void {
  const cfg = CONFIG.guard.distract;
  if (guard.distractedTicks > 0) {
    guard.distractedTicks -= 1;
    if (guard.distractedTicks === 0) {
      // Re-arm the interval clock so distractions stay spaced out.
      guard.distractCooldownTicks =
        cfg.minIntervalTicks + Math.floor(Math.random() * cfg.intervalJitterTicks);
    }
    return;
  }
  const eligible =
    guard.state === 'unaware' &&
    guard.awareness === 0 &&
    !guard.scanning &&
    !guard.isCaptain &&
    !guard.resting &&
    !guard.restorePower &&
    guard.bloodTrack === null;
  if (!eligible) return; // busy or agitated → clock frozen, never zones out
  if (guard.distractCooldownTicks > 0) {
    guard.distractCooldownTicks -= 1;
    return;
  }
  guard.distractedTicks = cfg.durationTicks + Math.floor(Math.random() * cfg.durationJitterTicks);
}

/**
 * Sniper-Elite search escalation. Once a guard has REVISITED its "seen" ghost —
 * arrived at the last-seen tile, the 5s ghost has expired, and it still can't see
 * the player or a blood trail — it looks for a fresher clue before committing to
 * a full alert. Returns what it did (for a one-off log), or null.
 *  1. Ask other guards: adopt the freshest live sighting on the floor.
 *  2. Otherwise → go ALERT + rouse the section (it then sweeps via `updateGuard`).
 * Run after awareness + the blood-track pass, before movement. Reuses
 * `broadcastCaution` so the whole section joins the search.
 */
export function escalateSearch(guard: Guard, guards: readonly Guard[]): 'adopted' | 'alerted' | null {
  if (guard.body.dead) return null;
  if (guard.seesPlayerNow) return null; // still has eyes → no escalation
  if (guard.state !== 'suspicious' && guard.state !== 'cautious') return null;
  if (guard.sightingGhostTicks > 0) return null; // ghost still fresh → keep revisiting
  if (guard.bloodTrack) return null; // found a blood clue → already tracking it
  if (guard.investigationX === null || guard.investigationY === null) return null;
  // Only escalate once the guard has actually reached the ghost spot.
  if (guard.x !== guard.investigationX || guard.y !== guard.investigationY) return null;

  // 1. Ask other guards — adopt the freshest live sighting.
  let peer: Guard | null = null;
  let best = 0;
  for (const g of guards) {
    if (g.id === guard.id || g.body.dead) continue;
    if (g.sightingGhostTicks > best && g.lastKnownPlayerX !== null) { best = g.sightingGhostTicks; peer = g; }
  }
  if (peer && peer.lastKnownPlayerX !== null) {
    guard.investigationX = peer.lastKnownPlayerX;
    guard.investigationY = peer.lastKnownPlayerY;
    guard.lastKnownPlayerX = peer.lastKnownPlayerX;
    guard.lastKnownPlayerY = peer.lastKnownPlayerY;
    guard.sightingGhostTicks = peer.sightingGhostTicks;
    if (guard.awareness < CONFIG.awareness.thresholds.cautious) guard.awareness = CONFIG.awareness.thresholds.cautious;
    guard.path = null;
    return 'adopted';
  }

  // 2. No clue → escalate to a floor sweep.
  guard.awareness = CONFIG.awareness.max;
  guard.state = 'alert';
  guard.investigationX = null; // clear the spent target so updateGuard sweep-searches
  guard.investigationY = null;
  broadcastCaution(guard, guards, CONFIG.awareness.broadcastRadius);
  return 'alerted';
}

/** Snap the guard's facing to the dominant axis of a delta vector. */
function faceToward(guard: Guard, dx: number, dy: number): void {
  if (dx === 0 && dy === 0) return;
  if (Math.abs(dx) >= Math.abs(dy)) {
    guard.facingDx = Math.sign(dx);
    guard.facingDy = 0;
  } else {
    guard.facingDx = 0;
    guard.facingDy = Math.sign(dy);
  }
}

/**
 * Bresenham line traversal; skips endpoints, returns true iff any interior
 * tile is a cubicle partition. Used to hide crouched targets behind
 * partitions per CLAUDE.md > Vision.
 */
export function partitionOnLine(
  map: MapData,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): boolean {
  if (x0 === x1 && y0 === y1) return false;
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;
  while (true) {
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      y += sy;
    }
    if (x === x1 && y === y1) return false;
    if (!inBounds(map, x, y)) return false;
    if (tileAt(map, x, y) === TileType.CubiclePartition) return true;
  }
}

/**
 * True if the guard currently has a shootable line of sight to the player,
 * accounting for FOV cone + cover-hides-crouched (partitions AND half walls).
 * Uses the guard's cached FOV set from the most recent updateGuardAwareness call.
 */
export function guardHasLosToPlayer(guard: Guard, player: Player, map: MapData): boolean {
  if (player.body.dead) return false;
  if (isBlinded(guard)) return false; // whited-out vision — can't draw a bead
  // Crouching behind cover hides the player from both the cone AND the 6th sense.
  if (player.stance === 'crouch' && hasCoverBetween(map, guard.x, guard.y, player.x, player.y)) {
    return false;
  }
  // Close-proximity 6th sense: the guard can hit a target right beside it even
  // in pitch darkness / out of its cone (bullets still stop at walls).
  const cheb = Math.max(Math.abs(guard.x - player.x), Math.abs(guard.y - player.y));
  if (cheb <= CONFIG.vision.proximityTiles) return true;
  // Otherwise: needs the player in its cone and enough light to draw a bead.
  if (!guard.fovTiles) return false;
  const idx = player.y * map.width + player.x;
  if (!guard.fovTiles.has(idx)) return false;
  // NV guards can draw a bead in the dark (boosted perceived light).
  if (visibilityFactor(perceivedLight(map, player.x, player.y, guard.nightVision)) <= 0) return false;
  return true;
}

/**
 * Propagate a cautious-state alert from `source` to every live guard within
 * `radius` (Chebyshev tiles). Recipients get bumped to at least the cautious
 * threshold and inherit the source's investigation target + last-known
 * player position. Called after per-guard awareness updates, so recipients'
 * own decay doesn't fight the broadcast on the same tick.
 */
export function broadcastCaution(source: Guard, guards: readonly Guard[], radius: number): void {
  if (source.investigationX === null || source.investigationY === null) return;
  const cautious = CONFIG.awareness.thresholds.cautious;
  for (const g of guards) {
    if (g.id === source.id) continue;
    if (g.body.dead) continue;
    const d = Math.max(Math.abs(g.x - source.x), Math.abs(g.y - source.y));
    if (d > radius) continue;
    // Bump awareness. Don't drop guards who are already alert.
    if (g.awareness < cautious) g.awareness = cautious;
    if (g.state === 'unaware' || g.state === 'suspicious') g.state = 'cautious';
    source.alertingTimer = Math.max(source.alertingTimer, 1.5); // show the # icon
    g.investigationX = source.investigationX;
    g.investigationY = source.investigationY;
    // Only overwrite lastKnown if the recipient doesn't have a fresher one.
    if (g.lastKnownPlayerX === null || g.lastKnownPlayerY === null) {
      g.lastKnownPlayerX = source.lastKnownPlayerX;
      g.lastKnownPlayerY = source.lastKnownPlayerY;
    }
    // Recipient will replan its path on the next tick because the target
    // changed vs cache.
    g.path = null;
  }
}

/**
 * Stand a guard down: clear its alarm and hunt state so it drops back to
 * `unaware` and its unaware behaviour (section patrol / captain roam) resumes.
 * Does not touch its position — it walks home on its own via `updateGuard`.
 */
export function standDown(guard: Guard): void {
  guard.awareness = 0;
  guard.state = 'unaware';
  guard.lastKnownPlayerX = null;
  guard.lastKnownPlayerY = null;
  guard.investigationX = null;
  guard.investigationY = null;
  guard.path = null;
}

/** Call the all-clear: stand down every still-agitated guard within `radius`. */
export function broadcastStandDown(source: Guard, guards: readonly Guard[], radius: number): void {
  source.alertingTimer = Math.max(source.alertingTimer, 1.5); // flash the "calling out" icon
  for (const g of guards) {
    if (g.id === source.id || g.body.dead || g.state === 'unaware') continue;
    const d = Math.max(Math.abs(g.x - source.x), Math.abs(g.y - source.y));
    if (d > radius) continue;
    standDown(g);
  }
}

/**
 * Elimination stand-down. Once the player is dead, a still-agitated guard that
 * CONFIRMS the kill — the corpse is in its lit view cone, or it's right beside
 * it — stands down and calls the all-clear to its section, so the whole force
 * returns to normal patrol. Guards that haven't confirmed keep converging on the
 * last-known spot (the corpse) until they confirm or catch the all-clear.
 * Returns a guard that newly confirmed this tick (for a one-off log line), else
 * null. Run after the per-guard awareness pass (needs fresh `fovTiles`).
 */
export function updateEliminationStandDown(
  guards: readonly Guard[],
  player: Player,
  map: MapData,
): Guard | null {
  if (!player.body.dead) return null;
  const corpseIdx = player.y * map.width + player.x;
  let confirmer: Guard | null = null;
  for (const g of guards) {
    if (g.body.dead || g.state === 'unaware') continue;
    const cheb = Math.max(Math.abs(g.x - player.x), Math.abs(g.y - player.y));
    const near = cheb <= CONFIG.vision.proximityTiles;
    const inCone =
      g.fovTiles?.has(corpseIdx) === true &&
      visibilityFactor(lightLevelAt(map, player.x, player.y)) > 0;
    if (!near && !inCone) continue;
    if (!confirmer) confirmer = g;
    standDown(g);
    broadcastStandDown(g, guards, CONFIG.awareness.broadcastRadius);
  }
  return confirmer;
}

/**
 * Force a guard to ENGAGE the player: snap to full awareness (alert), lock the
 * player's position as last-known + investigation target, and face them. Used
 * when a guard positively identifies the player at point-blank (a lit bump) or
 * is attacked by them.
 */
export function engageGuard(guard: Guard, player: Player): void {
  guard.awareness = CONFIG.awareness.max;
  guard.state = 'alert';
  guard.lastKnownPlayerX = player.x;
  guard.lastKnownPlayerY = player.y;
  guard.investigationX = player.x;
  guard.investigationY = player.y;
  faceToward(guard, player.x - guard.x, player.y - guard.y);
}

/**
 * A guard walked into the player (the player was blocking its step). It now
 * knows *someone* is right there — at minimum it investigates. If it can
 * actually make the player out (they're lit — dark = can't tell who), or the
 * player is already engaged, it treats them as a hostile and engages.
 */
export function guardBumpReaction(guard: Guard, player: Player, map: MapData): void {
  if (guard.body.dead || player.body.dead) return;
  const canSee = lightLevelAt(map, player.x, player.y) >= CONFIG.light.darkEps;
  if (canSee) {
    engageGuard(guard, player); // clearly an intruder → attack
    return;
  }
  // Dark bump — knows something is in the way but not what. Investigate.
  guard.investigationX = player.x;
  guard.investigationY = player.y;
  const cautious = CONFIG.awareness.thresholds.cautious;
  if (guard.awareness < cautious) guard.awareness = cautious;
  if (guard.state === 'unaware' || guard.state === 'suspicious') guard.state = 'cautious';
  faceToward(guard, player.x - guard.x, player.y - guard.y);
}

/**
 * Blackout response, run each tick. ONLY guards that personally end up in the
 * dark WITNESS the outage — they raise the alarm (go at least cautious, flash
 * the alerting icon) and notify nearby guards. The witness nearest the fusebox
 * is tasked to go flip the breakers back on. Guards who never see the dark stay
 * oblivious. When power is back, everyone stands down.
 */
export function updateBlackoutWitness(guards: readonly Guard[], map: MapData): void {
  if (!anyLightsOff(map)) {
    for (const g of guards) {
      g.restorePower = false;
      g.witnessedBlackout = false;
    }
    return;
  }
  const cautious = CONFIG.awareness.thresholds.cautious;
  const fb = map.fusebox;
  const hasRestorer = guards.some((g) => g.restorePower && !g.body.dead);
  let nearestWitness: Guard | null = null;
  let bestD = Infinity;
  for (const g of guards) {
    if (g.body.dead) continue;
    if (!isUnlit(map, g.x, g.y)) continue; // must actually be in the dark to witness
    if (!g.witnessedBlackout) {
      g.witnessedBlackout = true;
      if (g.awareness < cautious) g.awareness = cautious;
      if (g.state === 'unaware' || g.state === 'suspicious') g.state = 'cautious';
      g.alertingTimer = Math.max(g.alertingTimer, 1.5);
      notifyNearbyGuards(g, guards); // spread the word
    }
    if (fb) {
      const d = Math.max(Math.abs(g.x - fb.x), Math.abs(g.y - fb.y));
      if (d < bestD) { bestD = d; nearestWitness = g; }
    }
  }
  if (!hasRestorer && nearestWitness) {
    nearestWitness.restorePower = true;
    nearestWitness.alertingTimer = Math.max(nearestWitness.alertingTimer, 3);
    nearestWitness.path = null;
  }
}

/** Bump every live guard within the broadcast radius of `source` to cautious. */
function notifyNearbyGuards(source: Guard, guards: readonly Guard[]): void {
  const cautious = CONFIG.awareness.thresholds.cautious;
  for (const g of guards) {
    if (g.id === source.id || g.body.dead) continue;
    const d = Math.max(Math.abs(g.x - source.x), Math.abs(g.y - source.y));
    if (d > CONFIG.awareness.broadcastRadius) continue;
    if (g.awareness < cautious) g.awareness = cautious;
    if (g.state === 'unaware' || g.state === 'suspicious') g.state = 'cautious';
  }
}

/** Guard state readable string for HUD/debug. */
export function guardStateGlyph(s: GuardState): string {
  switch (s) {
    case 'unaware':
      return ' ';
    case 'suspicious':
      return '?';
    case 'cautious':
      return '!';
    case 'alert':
      return '‼';
  }
}
